import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { Skeleton, type DialogShellFooterPrimary, type ViewFetchState } from '@bliks-io/ui';
import {
  surveyApplies,
  variantAnswerCompletesSurvey,
  type GetSurveyResponse,
  type SubmitSurveyRequest,
  type SubmitVariantResponseRequest,
  type SurveyToggles,
  type SurveyWithVariantCheck,
  type VariantCheckAnswer
} from '@bliks-io/contracts';
import { ReviewModalLayout } from './ReviewModalLayout';
import { ReviewAlert } from './ReviewAlert';
import { ReviewUserSection } from './ReviewUserSection';
import { ReviewProcessDetails } from './ReviewProcessDetails';
import { StepPreviewWithProcessMap } from './StepPreviewWithProcessMap';
import { validateSurvey } from './surveyValidation';
import { SurveyQuestionsPage } from './SurveyFormSections';
import { VariantDescriptionSection, VariantVerdictSection } from './SurveyVariantSections';
import { frequencyOptions } from '@/lib/formatFrequency';
import { DialogLoadStateBody } from '@/components/dialogs/DialogLoadStateBody';

/**
 * What a saved Variant Check answer leaves: more questions, a finished survey,
 * or a decline — which ends the row too, but answers the who-else nomination
 * (ADR 0023) rather than finishing the feedback.
 */
export type VariantAnswerOutcome = 'continues' | 'finished' | 'declined';

function variantAnswerOutcome(
  answer: SubmitVariantResponseRequest,
  toggles: SurveyToggles
): VariantAnswerOutcome {
  if (answer.answer === 'not_performed') return 'declined';
  return variantAnswerCompletesSurvey(toggles) ? 'finished' : 'continues';
}

interface SurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (response: SubmitSurveyRequest) => void;
  /**
   * The Variant Check answer, which is saved on its own endpoint the moment it
   * is given (plan 029) — never together with the classic questions. Rejects
   * when the save was refused, which is what keeps the dialog on its page.
   * The dialog closes once the save lands unless the outcome is `continues`.
   */
  onVariantAnswer: (
    answer: SubmitVariantResponseRequest,
    outcome: VariantAnswerOutcome
  ) => Promise<void> | void;
  isSubmitting?: boolean;
  /**
   * The fetched response whole — the survey together with the toggles the
   * SERVER computed for its tenant. One prop rather than two, because the
   * toggles decide whether "Ja, genau so" still has a second page to show: a
   * dialog that could be handed a survey without them would read a missing
   * `classic` as "nothing left to ask" and complete the row with no classic
   * answers, and a verdict is immutable (ADR 0022).
   */
  surveyResponse?: GetSurveyResponse | null;
  /** Why `surveyResponse` is missing; a loaded response always wins over it. */
  loadState: ViewFetchState;
}

export function SurveyDialog({
  open,
  onOpenChange,
  onSubmit,
  onVariantAnswer,
  isSubmitting = false,
  surveyResponse,
  loadState
}: SurveyDialogProps) {
  const { t } = useTranslation();

  const [response, setResponse] = useState<SubmitSurveyRequest>({});
  const [submitted, setSubmitted] = useState(false);
  const [verdict, setVerdict] = useState<VariantCheckAnswer | undefined>(undefined);
  // Local, and never part of the classic request: "Ja, aber anders" is the one
  // answer with a second page, and its text is saved with the answer itself.
  const [describingVariant, setDescribingVariant] = useState(false);
  const [variantDescription, setVariantDescription] = useState('');
  // A verdict is immutable (ADR 0022), so a saved one must not be offered
  // again in the window between the save and the refetch that swaps the page.
  const [verdictSaved, setVerdictSaved] = useState(false);

  // One decision, both facts: whether the row can be answered, and — when it
  // cannot — why, so the read-only copy is a byproduct of the same check rather
  // than re-derived from `status` alone (which would show the wrong reason the
  // moment answerability grew a new condition).
  const dialogState = classifySurvey(surveyResponse);
  const answerable = dialogState?.kind === 'answerable' ? dialogState.response : undefined;

  // The Variant Check is a page of its own ahead of the classic questions, and
  // whether it is still owed is the server's call (`awaitsVariantVerdict`),
  // never re-derived here: the answer is saved on its own endpoint, so the page
  // gives way once the refetched response says it was.
  const showVariantQuestion = answerable !== undefined && answerable.survey.awaitsVariantVerdict;

  // The description page belongs to the verdict, so it ends with it: once the
  // refetched row no longer owes one, the footer is the classic form's again.
  const describing = showVariantQuestion && describingVariant;
  // Saved but not yet refetched counts as still in flight for the footer.
  const savePending = isSubmitting || verdictSaved;

  const options = frequencyOptions(t);

  // Validation. Takes the response as an argument so the verdict page can
  // validate an answer it has only just built, before state has settled.
  const validate = useCallback(
    (candidate: SubmitSurveyRequest, toggles: SurveyToggles) =>
      validateSurvey(
        candidate,
        {
          enableRatingAndComment: toggles.rating,
          enableFrequencyMeasurement: toggles.frequency,
          enableDurationMeasurement: toggles.duration
        },
        t
      ),
    [t]
  );

  const validation = useMemo<Record<string, string>>(
    () => (answerable ? validate(response, answerable.toggles) : {}),
    [validate, response, answerable]
  );

  // The single submit seam — every path to `onSubmit` validates first, so a
  // rule added to validateSurvey cannot be bypassed by one of them.
  const submitIfValid = (candidate: SubmitSurveyRequest, toggles: SurveyToggles) => {
    setSubmitted(true);

    if (Object.keys(validate(candidate, toggles)).length > 0) {
      return;
    }

    onSubmit(candidate);
  };

  /**
   * Saves the verdict and closes on success when it left nothing else to ask —
   * a decline ends the survey, and so does any verdict in a tenant with no
   * classic question switched on. The toggles that decide this ride with the
   * row, so the decision belongs here rather than at the page that hosts the
   * dialog. A refused save keeps the page, and the typed text, as they are.
   */
  const saveVerdict = async (answer: SubmitVariantResponseRequest, toggles: SurveyToggles) => {
    const outcome = variantAnswerOutcome(answer, toggles);
    try {
      await onVariantAnswer(answer, outcome);
    } catch {
      return;
    }
    setVerdictSaved(true);

    if (outcome !== 'continues') {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setResponse({});
    setVerdict(undefined);
    setDescribingVariant(false);
    setVariantDescription('');
    setVerdictSaved(false);
    setSubmitted(false);
    onOpenChange(false);
  };

  const primaryAction = (): DialogShellFooterPrimary | undefined => {
    if (!answerable) return undefined;

    if (describing) {
      return {
        label: t('common.save'),
        onClick: () => {
          void saveVerdict(
            { answer: 'different', description: variantDescription },
            answerable.toggles
          );
        },
        disabled: !variantDescription.trim(),
        loading: savePending
      };
    }

    if (showVariantQuestion) {
      // "Ja, aber anders" owes a text before it can be saved; the other two
      // answers save straight from the verdict page.
      if (verdict === 'different') {
        return { label: t('common.continue'), onClick: () => setDescribingVariant(true) };
      }
      return {
        label: t('common.save'),
        onClick: verdict
          ? () => void saveVerdict({ answer: verdict }, answerable.toggles)
          : undefined,
        disabled: verdict === undefined,
        loading: savePending
      };
    }

    return {
      label: t('surveyDialog.submit'),
      onClick: () => submitIfValid(response, answerable.toggles),
      loading: isSubmitting
    };
  };

  const recording = readPopulatedRecording(surveyResponse?.survey);

  return (
    <ReviewModalLayout
      open={open}
      onOpenChange={onOpenChange}
      icon={MessageSquare}
      title={t('surveyDialog.title')}
      description={t('surveyDialog.description')}
      footer={{
        secondary: describing
          ? {
              // Live under the save latch too: the verdict is already stored,
              // so a refetch that never lands must not trap the recipient on a
              // spinning page.
              label: t('common.back'),
              onClick: () => setDescribingVariant(false),
              disabled: isSubmitting
            }
          : {
              label: t('surveyDialog.cancel'),
              onClick: handleCancel,
              disabled: isSubmitting
            },
        primary: primaryAction()
      }}
    >
      {!surveyResponse && (
        <DialogLoadStateBody
          loadState={loadState}
          notFoundKey="errors.survey.notFound"
          skeleton={<SurveySkeleton />}
        />
      )}

      {/* Context Section - Show who recorded this step and which process */}
      {recording && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:items-stretch">
            <div className="md:col-span-2">
              <ReviewUserSection label={t('surveyDialog.recordedBy')} user={recording.recordedBy} />
            </div>
            <div className="md:col-span-3">
              <ReviewProcessDetails process={recording.process} />
            </div>
          </div>

          {/* Process Step Preview */}
          <StepPreviewWithProcessMap
            label={t('surveyDialog.processStep')}
            sourceProcessStep={recording.stepRecording}
            showOnlyCurrentStep
            hostOpen={open}
            processId={recording.process._id}
            // The survey is about a finalized recording, so its own id is the
            // node in the published diagram.
            highlightNodeId={recording.stepRecording._id}
          />
        </>
      )}

      {describing && (
        <VariantDescriptionSection
          value={variantDescription}
          onChange={setVariantDescription}
          disabled={savePending}
          t={t}
        />
      )}

      {showVariantQuestion && !describing && (
        <VariantVerdictSection value={verdict} onChange={setVerdict} disabled={savePending} t={t} />
      )}

      {answerable && !showVariantQuestion && (
        <SurveyQuestionsPage
          response={response}
          onResponseChange={setResponse}
          frequencyOptions={options}
          validation={validation}
          submitted={submitted}
          enableRatingAndComment={answerable.toggles.rating}
          enableFrequencyMeasurement={answerable.toggles.frequency}
          enableDurationMeasurement={answerable.toggles.duration}
          t={t}
        />
      )}

      {dialogState?.kind === 'closed' && (
        <ReviewAlert
          variant="info"
          title={t('surveyDialog.closedTitle')}
          description={t(dialogState.reasonKey)}
        />
      )}
    </ReviewModalLayout>
  );
}

function SurveySkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Skeleton className="h-24 md:col-span-2" />
        <Skeleton className="h-24 md:col-span-3" />
      </div>
      <Skeleton className="h-48" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    </>
  );
}

type SurveyDialogState =
  | { kind: 'answerable'; response: GetSurveyResponse }
  | { kind: 'closed'; reasonKey: string };

/**
 * The dialog's whole decision in one place: a row is answerable, or it is
 * read-only for a stated reason. A dismissed row carries an earlier decline; a
 * completed one carries saved answers; a still-pending row the tenant's toggles
 * ask this recipient nothing on is the step's own author under variant-only
 * settings — `surveyApplies` is the SAME predicate the backend readers gate on,
 * off the server's toggles, so the dialog cannot complete a row My Tasks would
 * never have offered. Both facts fall out of one pass, so the read-only copy
 * can never disagree with why the form was withheld.
 *
 * Terminal states are checked before applicability: a completed or dismissed
 * row is a real answer whatever the toggles say today.
 */
function classifySurvey(
  response: GetSurveyResponse | null | undefined
): SurveyDialogState | undefined {
  if (!response) return undefined;
  const { status, awaitsVariantVerdict } = response.survey;
  if (status === 'dismissed') return { kind: 'closed', reasonKey: 'surveyDialog.closedDismissed' };
  if (status === 'completed') return { kind: 'closed', reasonKey: 'surveyDialog.closedCompleted' };
  if (!surveyApplies(response.toggles, awaitsVariantVerdict)) {
    return { kind: 'closed', reasonKey: 'surveyDialog.closedNotApplicable' };
  }
  return { kind: 'answerable', response };
}

/**
 * The survey's recording, its recorder and its process — but only when all
 * three came back populated, which is the one shape the context section can
 * render.
 */
function readPopulatedRecording(survey: SurveyWithVariantCheck | undefined) {
  const stepRecording = survey?.stepRecording;
  if (typeof stepRecording !== 'object' || stepRecording === null) return undefined;

  const { recordedBy, process } = stepRecording;
  if (typeof recordedBy !== 'object' || recordedBy === null) return undefined;
  if (typeof process !== 'object' || process === null) return undefined;

  return { stepRecording, recordedBy, process };
}
